/**
 * Zestawienie źródeł ciepła WIELU budynków (właściciel: „podsumowanie per budynek 1, 2, 3, 4",
 * kontrakt #326) — czyste helpery dla modala i eksportu CSV. Dane: POST /buildings/heat-source-totals.
 * Etykiety typów wyłącznie z lustra `ds/HeatSourceLabels.ts` (bez ręcznych nazw).
 */
import type { HeatSourceTotals } from '../types';
import type { HeatSourceTotalsBulkResponse } from '../services/api';
import { HEAT_SOURCE_TYPE_ORDER, resolveHeatSourceType } from '../ds/HeatSourceLabels';
import { excelCsv } from './download';

/**
 * Kolumny tabeli = typy obecne w sumie, w kolejności słownika UI; typy spoza słownika
 * (np. `unknown`) na końcu alfabetycznie. Typ z zerem wszędzie nie dostaje kolumny.
 */
export function heatSourceSummaryColumns(data: HeatSourceTotalsBulkResponse): string[] {
  const present = new Set(data.totals.byType.filter((t) => t.count > 0).map((t) => t.type));
  const known = HEAT_SOURCE_TYPE_ORDER.filter((t) => present.has(t));
  const rest = Array.from(present).filter((t) => !HEAT_SOURCE_TYPE_ORDER.includes(t)).sort();
  return [...known, ...rest];
}

export function countOfType(totals: HeatSourceTotals, type: string): number {
  return totals.byType.find((t) => t.type === type)?.count ?? 0;
}

/** Nagłówek kolumny typu — skrót ze słownika, nierozpoznany typ bez zmian. */
export function heatSourceColumnLabel(type: string): string {
  if (type === 'unknown') return 'Nieokreślony typ';
  return resolveHeatSourceType(type) || type;
}

/**
 * CSV dla Excela w polskich ustawieniach: separator `;`, BOM (UTF-8 z ogonkami), CRLF.
 * Wiersze = budynki, kolumny = typy + „Razem" + „Lokale bez danych", ostatni wiersz = suma.
 */
export function heatSourceSummaryCsv(data: HeatSourceTotalsBulkResponse): string {
  const cols = heatSourceSummaryColumns(data);
  const header = ['Budynek', ...cols.map(heatSourceColumnLabel), 'Razem', 'Lokale bez danych'];
  const row = (label: string, t: HeatSourceTotals) => [
    label, ...cols.map((c) => countOfType(t, c)), t.total, t.apartmentsUnknown,
  ];
  const lines = [
    header,
    ...data.buildings.map((b) => row(b.address, b.totals)),
    row('Suma', data.totals),
  ];
  return excelCsv(lines);
}
